import { forwardRef, useImperativeHandle } from 'react';
import { TouchableOpacity, View } from 'react-native';
import StyleSheet from 'react-native-media-query';
import { Icon, Layout, Text } from '@ui-kitten/components';
import dayjs from 'dayjs';
import lodashIsEmpty from 'lodash/isEmpty';
import lodashIsEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import constants from '../../../Config/constants';
import { pluralize } from '../../../Helper';
import CheckoutHelper from '../../../Helper/Checkout';
import useMealPlan from '../../../Hooks/useMealPlan';
import Service from '../../../Service';
import ThemeColor from '../../../Theme/colors';
import ThemeStyle from '../../../Theme/styles';
import BreakPoints from '../../../Theme/styles/breakpoints';
import { SECTION_TITLE_LIGHT } from '../../../Theme/typographyProps';

import useCheckoutHandler from '../controller/useCheckoutHandler';
import checkoutStyle from '../styles';

import useDeliveryEstimate from './index.controller';

const { ORDER_TYPES } = constants;

const ASAP = CheckoutHelper.ASAP;

const DeliveryEstimate = forwardRef((props, ref) => {
  const { disabled, storeHours, storeOffDates } = props;
  const { isMealPlan } = useMealPlan();
  const { checkoutInfo, orderType } = useCheckoutHandler();
  const { value, onDeliveryScheduleChanged, showScheduleListModal } =
    useDeliveryEstimate(storeHours);

  const dateOptions = Service.generateDateOptions({
    storeHours: storeHours,
    offDates: storeOffDates,
    daysInAdvance: checkoutInfo.days_accepting_in_advance,
    prepTime: checkoutInfo.prep_time,
    asap: ASAP,
  });

  useImperativeHandle(ref, () => ({
    _getDateOptions() {
      return dateOptions;
    },
  }));

  const canAsap = lodashIsEqual(dateOptions[0], ASAP);

  const isStandard = value?.value === ASAP.value || lodashIsEmpty(value);

  const maximumDate = dateOptions[dateOptions.length - 1];

  const isPickup = orderType?.value?.includes(ORDER_TYPES.PICKUP);

  const hasPreorderDate = !!maximumDate?.value;
  const cantAsapAndNoAdvanceOrder = !canAsap && !hasPreorderDate;

  const _onChangeDeliverySchedule = (type) => () => {
    if (type === ASAP.label) {
      onDeliveryScheduleChanged(ASAP);
    } else {
      showScheduleListModal();
    }
  };

  if (isMealPlan) return null;

  return (
    <Layout>
      <View style={ThemeStyle.pageVerticalSpacingMedium}>
        <View style={checkoutStyle.webBox}>
          <View
            style={[
              ThemeStyle.flexDirectionRowCenterSpaceBetween,
              ThemeStyle.spacingBottomMedium,
            ]}
          >
            <Text {...SECTION_TITLE_LIGHT}>
              {isPickup ? 'Prep time estimate' : 'Delivery Schedule'}
            </Text>

            {isPickup && (
              <Text category="p2">
                {checkoutInfo.prep_time}{' '}
                {pluralize(checkoutInfo.prep_time, 'minute')}
              </Text>
            )}
          </View>

          <View
            dataSet={{ media: ids.deliveryScheduleContainer }}
            style={styles.deliveryScheduleContainer}
          >
            {(canAsap || cantAsapAndNoAdvanceOrder) && (
              <TouchableOpacity
                disabled={disabled}
                onPress={_onChangeDeliverySchedule(ASAP.label)}
                style={
                  isStandard
                    ? styles.deliveryScheduleSelectedCardContainer
                    : styles.deliveryScheduleUnselectedCardContainer
                }
              >
                <View>
                  <View style={styles.deliveryCardRowHeader}>
                    <Text category="p2">NOW (ASAP ORDER)</Text>
                    {isStandard && (
                      <Icon
                        name="checkmark"
                        fill={ThemeColor.black}
                        style={styles.iconCheck}
                      />
                    )}
                  </View>
                </View>

                <Text category="c1" status="primary">
                  {`${dayjs().format(constants.DATE_DISPLAY_FORMAT)} (ASAP)`}
                </Text>
              </TouchableOpacity>
            )}

            {(hasPreorderDate || cantAsapAndNoAdvanceOrder) && (
              <TouchableOpacity
                disabled={disabled}
                onPress={_onChangeDeliverySchedule('Scheduled')}
                style={
                  !isStandard
                    ? styles.deliveryScheduleSelectedCardContainer
                    : styles.deliveryScheduleUnselectedCardContainer
                }
              >
                <View>
                  <View style={styles.deliveryCardRowHeader}>
                    <Text category="p2">SCHEDULED</Text>
                    {!isStandard && (
                      <Icon
                        name="checkmark"
                        fill={ThemeColor.black}
                        style={styles.iconCheck}
                      />
                    )}
                  </View>
                </View>
                {!isStandard ? (
                  <View
                    dataSet={{ media: ids.deliveryScheduleContainer }}
                    style={styles.deliveryScheduleContainer}
                  >
                    <View>
                      <Text category="c1" status="primary">
                        Date
                      </Text>
                      <Text category="c1" status="primary">
                        {dayjs(value?.date).format('dddd, MMM DD')}
                      </Text>
                    </View>

                    <View>
                      <Text category="c1" status="primary">
                        Time
                      </Text>
                      <Text category="c1" status="primary">
                        {`${dayjs(value?.time, 'hh:mm').format(
                          constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT,
                        )} - ${dayjs(value?.time, 'hh:mm')
                          .add(30, 'minutes')
                          .format(constants.TIME_ONLY_DISPLAY_CAPITAL_FORMAT)}`}
                      </Text>
                    </View>
                  </View>
                ) : (
                  <Text category="c1" status="primary">
                    Select Schedule for later
                  </Text>
                )}
              </TouchableOpacity>
            )}
          </View>
        </View>
      </View>
    </Layout>
  );
});
const { ids, styles } = StyleSheet.create({
  deliveryScheduleUnselectedCardContainer: {
    borderWidth: 1,
    borderColor: ThemeColor.lightGray,
    minWidth: 300,
    minHeight: 105,
    padding: 20,
    borderRadius: 14,
  },
  deliveryScheduleSelectedCardContainer: {
    borderWidth: 1,
    borderColor: ThemeColor.green,
    minWidth: 300,
    minHeight: 105,
    padding: 20,
    borderRadius: 14,
  },
  deliveryScheduleContainer: {
    rowGap: 10,
    columnGap: 10,
    [`@media ${BreakPoints.xs}`]: {
      flexDirection: 'column',
    },
    [`@media ${BreakPoints.sm}`]: {
      flexDirection: 'column',
    },
    [`@media ${BreakPoints.md}`]: {
      flexDirection: 'column',
    },
    [`@media ${BreakPoints.lg}`]: {
      flexDirection: 'column',
    },
    [`@media ${BreakPoints.xl}`]: {
      flexDirection: 'column',
    },
    [`@media ${BreakPoints.xxl}`]: {
      flexDirection: 'row',
    },
  },
  deliveryCardRowHeader: {
    ...ThemeStyle.flexDirectionRowSpaceBetween,
    ...ThemeStyle.spacingBottomSmall,
    height: 20,
  },
  iconCheck: { width: 20, height: 20 },
  iconCalendar: { width: 15, height: 15 },
});

DeliveryEstimate.propTypes = {
  value: PropTypes.object,
  disabled: PropTypes.bool,
  storeHours: PropTypes.array.isRequired,
  storeOffDates: PropTypes.array.isRequired,
};

export default DeliveryEstimate;
